import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Select, { SingleValue } from 'react-select'
import LoraChart, { LoraChartHandle, LoraDataPoint, LoraChartStats } from './LoraChart'
import LoraSummaryCards from './LoraSummaryCards'
import FilterButton from '../charts/components/FilterButton'
import { DateRange, Range } from 'react-date-range'
import moment from 'moment'
import { Modal, Button } from 'react-bootstrap'

type DatePreset = 'today' | 'yesterday' | 'custom'

type DeviceOption = {
  value: string
  label: string
  deviceId: string
}

const DEVICES: DeviceOption[] = [
  { value: 'dev-1', label: 'eui-70b3d54a000042d3', deviceId: 'eui-70b3d54a000042d3' },
  { value: 'dev-2', label: 'test-device-1', deviceId: 'eui-70b3d54a000042d3' },
  { value: 'dev-3', label: 'test-device-2', deviceId: 'eui-70b3d54a000042d3' },
]

function presetToRange(preset: DatePreset): Range {
  const key = 'selection'
  if (preset === 'today') {
    const d = moment().startOf('day').toDate()
    return { key, startDate: d, endDate: d }
  }
  if (preset === 'yesterday') {
    const d = moment().subtract(1, 'day').startOf('day').toDate()
    return { key, startDate: d, endDate: d }
  }
  const d = moment().startOf('day').toDate()
  return { key, startDate: d, endDate: d }
}

function filterIdToPreset(id: string): DatePreset | null {
  if (id === 'current') return 'today'
  if (id === 'yesterday') return 'yesterday'
  if (id === 'custom') return 'custom'
  return null
}

function presetToFilterId(preset: DatePreset): string {
  if (preset === 'today') return 'current'
  if (preset === 'yesterday') return 'yesterday'
  return 'custom'
}

function exportLoraCsv(points: LoraDataPoint[], deviceId: string, dateYmd: string) {
  const rows = [
    'timestamp,value',
    ...points.map((p) => `${moment(p.x).format('YYYY-MM-DD HH:mm:ss')},${p.y}`),
  ]
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `lora-${deviceId}-${dateYmd}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function LoraDashboard() {
  const intl = useIntl()
  const chartRef = useRef<LoraChartHandle>(null)

  const [selectedDevice, setSelectedDevice] = useState<DeviceOption | null>(DEVICES[0] ?? null)
  const [datePreset, setDatePreset] = useState<DatePreset>('today')
  const [range, setRange] = useState<Range>(() => presetToRange('today'))

  const isSingleDay = useMemo(() => {
    if (!range.startDate || !range.endDate) return false
    return moment(range.startDate).isSame(range.endDate, 'day')
  }, [range])

  const [startTime, setStartTime] = useState<string>('00:00')
  const [endTime, setEndTime] = useState<string>('23:59')

  const [showFilters, setShowFilters] = useState(false)
  const [modalRange, setModalRange] = useState<Range>(range)
  const [modalStartTime, setModalStartTime] = useState<string>(startTime)
  const [modalEndTime, setModalEndTime] = useState<string>(endTime)
  const [modalDevice, setModalDevice] = useState<DeviceOption | null>(selectedDevice)

  const [stats, setStats] = useState<LoraChartStats>({})
  const [chartData, setChartData] = useState<LoraDataPoint[]>([])
  const [currentViewDay, setCurrentViewDay] = useState<string>('')
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [isChartLoading, setIsChartLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<string>('')
  const [liveMode, setLiveMode] = useState(false)
  const [isChartFullscreen, setIsChartFullscreen] = useState(false)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  )

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const includesToday = useMemo(() => {
    if (!range.startDate || !range.endDate) return false
    const now = moment()
    return (
      moment(range.startDate).isSameOrBefore(now, 'day') &&
      moment(range.endDate).isSameOrAfter(now, 'day')
    )
  }, [range])

  useEffect(() => {
    if (!includesToday) setLiveMode(false)
  }, [includesToday])

  const activeFilterId = presetToFilterId(datePreset)

  const toolbarDateLabel = useMemo(() => {
    const s = range.startDate ? moment(range.startDate).format('DD MMM YYYY') : '-'
    const e = range.endDate ? moment(range.endDate).format('DD MMM YYYY') : '-'
    const base = s === e ? s : `${s} — ${e}`
    if (isSingleDay) {
      return `${base} | ${startTime} - ${endTime}`
    }
    return base
  }, [range, isSingleDay, startTime, endTime])

  const viewingDayLabel = useMemo(() => {
    if (!currentViewDay) return '—'
    return moment(currentViewDay, 'YYYY-MM-DD').format('D MMM YYYY')
  }, [currentViewDay])

  const handlePresetClick = (id: string) => {
    const preset = filterIdToPreset(id)
    if (!preset) return
    if (preset === 'custom') {
      setDatePreset('custom')
      setModalRange(range)
      setModalStartTime(startTime)
      setModalEndTime(endTime)
      setModalDevice(selectedDevice)
      setShowFilters(true)
      return
    }
    setDatePreset(preset)
    setRange(presetToRange(preset))
  }

  const openFilters = () => {
    setModalRange(range)
    setModalStartTime(startTime)
    setModalEndTime(endTime)
    setModalDevice(selectedDevice)
    setShowFilters(true)
  }

  const closeFilters = () => setShowFilters(false)

  const applyFilters = () => {
    setRange(modalRange)
    setStartTime(modalStartTime)
    setEndTime(modalEndTime)
    if (modalDevice) setSelectedDevice(modalDevice)
    setDatePreset('custom')
    setShowFilters(false)
  }

  const quickHourPreset = (hours: number) => {
    if (!modalRange.startDate) return
    const start = moment(modalRange.startDate).startOf('day')
    const end = moment(start).add(hours, 'hours').subtract(1, 'minute')
    setModalStartTime(start.format('HH:mm'))
    setModalEndTime(end.format('HH:mm'))
  }

  const isModalSingleDay = useMemo(() => {
    return Boolean(
      modalRange.startDate &&
        modalRange.endDate &&
        moment(modalRange.startDate).isSame(modalRange.endDate, 'day')
    )
  }, [modalRange])

  const isInvalidHourRange = useMemo(() => {
    return Boolean(
      isModalSingleDay && modalStartTime && modalEndTime && modalStartTime >= modalEndTime
    )
  }, [isModalSingleDay, modalStartTime, modalEndTime])

  const handleStats = useCallback((next: LoraChartStats) => {
    setStats(next)
    if (next.current != null) {
      setLastUpdated(moment().format('HH:mm:ss'))
    }
  }, [])

  const handleDataChange = useCallback((points: LoraDataPoint[]) => {
    setChartData(points)
  }, [])

  const handleExportCsv = () => {
    if (!selectedDevice || chartData.length === 0) return
    const dateYmd = currentViewDay || moment(range.startDate).format('YYYY-MM-DD')
    exportLoraCsv(chartData, selectedDevice.deviceId, dateYmd)
  }

  const handleRetry = () => {
    setFetchError(null)
    chartRef.current?.reload()
  }

  const renderDeviceSelect = (forModal: boolean) => (
    <div
      className={forModal ? 'col-12 mb-3' : 'flex-grow-1'}
      style={{ minWidth: forModal ? undefined : 220 }}
    >
      <label className='fw-bold fs-7 text-gray-500 mb-1 d-block'>
        {intl.formatMessage({ id: 'LORA.DEVICE' })}
      </label>
      <Select
        value={forModal ? modalDevice : selectedDevice}
        onChange={(val: SingleValue<DeviceOption>) => {
          if (forModal) setModalDevice(val)
          else setSelectedDevice(val)
        }}
        options={DEVICES}
        placeholder={intl.formatMessage({ id: 'LORA.SELECT_DEVICE' })}
      />
    </div>
  )

  const filterModalBody = (
    <div className='row g-4'>
      {isMobile ? renderDeviceSelect(true) : null}
      <div className='col-12 col-lg-auto'>
        <span className='fw-bold fs-6 text-gray-400 d-block mb-2'>
          {intl.formatMessage({ id: 'DATERANGE' })}
        </span>
        <div className='border rounded p-2'>
          <DateRange
            ranges={[modalRange]}
            onChange={(r) => {
              const sel = r.selection
              setModalRange({
                key: 'selection',
                startDate: sel.startDate,
                endDate: sel.endDate,
              })
            }}
            moveRangeOnFirstSelection={false}
          />
        </div>
      </div>

      {isModalSingleDay ? (
        <div className='col-12 col-lg'>
          <span className='fw-bold fs-6 text-gray-400 d-block mb-2'>
            {intl.formatMessage({ id: 'LORA.HOUR_RANGE', defaultMessage: 'Hour Range' })}
          </span>
          <div className='d-flex flex-wrap align-items-end gap-3 mb-3'>
            <div>
              <span className='fw-bold fs-7 text-gray-500 d-block mb-1'>
                {intl.formatMessage({ id: 'COMMON.START' })}
              </span>
              <input
                type='time'
                className='form-control'
                min='00:00'
                max='23:59'
                value={modalStartTime}
                onChange={(e) => setModalStartTime(e.target.value)}
              />
            </div>
            <div>
              <span className='fw-bold fs-7 text-gray-500 d-block mb-1'>
                {intl.formatMessage({ id: 'COMMON.END' })}
              </span>
              <input
                type='time'
                className='form-control'
                min='00:00'
                max='23:59'
                value={modalEndTime}
                onChange={(e) => setModalEndTime(e.target.value)}
              />
            </div>
          </div>
          {isInvalidHourRange ? (
            <div className='text-danger small mb-2'>
              {intl.formatMessage({
                id: 'LORA.INVALID_HOUR_RANGE',
                defaultMessage: 'Start hour must be before end hour.',
              })}
            </div>
          ) : null}
          <div className='d-flex flex-wrap gap-2'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => quickHourPreset(1)}
            >
              1h
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => quickHourPreset(6)}
            >
              6h
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => quickHourPreset(12)}
            >
              12h
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => quickHourPreset(24)}
            >
              24h
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )

  const activePresetLabel = useMemo(() => {
    return intl.formatMessage({ id: activeFilterId, defaultMessage: activeFilterId })
  }, [intl, activeFilterId])

  const mobileContextLabel = useMemo(() => {
    if (isSingleDay) {
      return `${viewingDayLabel} · ${startTime}–${endTime}`
    }
    return toolbarDateLabel
  }, [isSingleDay, viewingDayLabel, startTime, endTime, toolbarDateLabel])

  const renderDayNavigation = (size: 'sm' | 'md' = 'md') => (
    <div className='d-flex align-items-center gap-2'>
      <button
        type='button'
        className={`btn btn-${size === 'sm' ? 'sm ' : ''}btn-light btn-icon`}
        style={{ minWidth: 44, minHeight: 44 }}
        onClick={() => chartRef.current?.navigateDay('backward')}
        disabled={isChartLoading || !selectedDevice}
        aria-label={intl.formatMessage({ id: 'LORA.LOAD_PREV' })}
      >
        <i className='fa fa-chevron-left'></i>
      </button>
      <span
        className={`fw-bold text-gray-700 px-1 ${size === 'sm' ? 'fs-7' : 'fs-6'}`}
        style={{ minWidth: size === 'sm' ? 96 : 120, textAlign: 'center' }}
      >
        {viewingDayLabel}
      </span>
      <button
        type='button'
        className={`btn btn-${size === 'sm' ? 'sm ' : ''}btn-light btn-icon`}
        style={{ minWidth: 44, minHeight: 44 }}
        onClick={() => chartRef.current?.navigateDay('forward')}
        disabled={isChartLoading || !selectedDevice}
        aria-label={intl.formatMessage({ id: 'LORA.LOAD_NEXT' })}
      >
        <i className='fa fa-chevron-right'></i>
      </button>
    </div>
  )

  const renderLiveToggle = (compact = false) =>
    includesToday ? (
      <button
        type='button'
        className={`btn ${
          compact ? 'btn-sm btn-light' : 'btn-sm btn-light'
        } d-flex align-items-center gap-2`}
        style={{ minHeight: compact ? 44 : undefined }}
        onClick={() => setLiveMode(!liveMode)}
        aria-pressed={liveMode}
        aria-label={liveMode ? 'LIVE' : 'Live Mode Disabled'}
      >
        <span
          style={{
            position: 'relative',
            display: 'inline-block',
            width: 36,
            height: 20,
            borderRadius: 10,
            backgroundColor: liveMode ? '#f44336' : '#cdd1d8',
            transition: 'background-color 0.25s ease',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: liveMode ? 18 : 2,
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              transition: 'left 0.25s ease',
            }}
          />
        </span>
        {!compact ? (
          <span className='d-flex align-items-center gap-1'>
            {liveMode ? (
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#f44336',
                  boxShadow: '0 0 6px 2px rgba(244,67,54,0.4)',
                  animation: 'lora-live-blink 1s ease-in-out infinite',
                }}
              />
            ) : null}
            <span className='fw-bold fs-7 text-gray-700'>
              {liveMode ? 'LIVE' : 'Live Mode Disabled'}
            </span>
          </span>
        ) : liveMode ? (
          <span className='fw-bold fs-8 text-danger'>LIVE</span>
        ) : null}
      </button>
    ) : null

  return (
    <div>
      <style>{`
        @keyframes lora-live-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
      `}</style>

      <div className='card'>
        <div className='card-header border-0 pt-5 flex-column flex-lg-row align-items-start align-items-lg-center gap-3'>
          <h3 className='card-title align-items-start flex-column mb-0'>
            <span className='card-label fw-bold fs-3'>
              {intl.formatMessage({ id: 'MENU.LORA.DASHBOARD' })}
            </span>
          </h3>

          {!isMobile ? (
            <div className='card-toolbar flex-wrap gap-1' data-kt-buttons='true'>
              <FilterButton
                id='current'
                value='Current'
                isActive={activeFilterId === 'current'}
                onClick={handlePresetClick}
              />
              <FilterButton
                id='yesterday'
                value='Yesterday'
                isActive={activeFilterId === 'yesterday'}
                onClick={handlePresetClick}
              />
              <FilterButton
                id='custom'
                value='Custom'
                isActive={activeFilterId === 'custom'}
                onClick={handlePresetClick}
              />
            </div>
          ) : null}
        </div>

        <div className='card-body'>
          {isMobile ? (
            <div className='mb-4'>
              <div className='d-flex align-items-stretch gap-2 mb-3'>
                <button
                  type='button'
                  className='btn btn-light-primary flex-grow-1 text-start d-flex flex-column justify-content-center py-3 px-3'
                  onClick={openFilters}
                >
                  <span className='d-flex align-items-center fw-bold fs-7'>
                    <i className='fa fa-filter me-2'></i>
                    {intl.formatMessage({ id: 'FILTERS.OPTIONS' })}
                  </span>
                  <span className='fs-8 text-gray-600 text-truncate mt-1'>
                    {activePresetLabel} · {mobileContextLabel}
                  </span>
                </button>
                <div className='d-flex flex-row gap-2 flex-shrink-0 align-items-stretch'>
                  {renderLiveToggle(true)}
                </div>
              </div>

              <div
                className='d-flex gap-1 overflow-auto pb-1 mb-3 flex-nowrap'
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {(['current', 'yesterday', 'custom'] as const).map((id) => (
                  <FilterButton
                    key={id}
                    id={id}
                    value={id}
                    isActive={activeFilterId === id}
                    onClick={handlePresetClick}
                  />
                ))}
              </div>

              <div className='d-flex flex-column align-items-center gap-2'>
                {renderDayNavigation('sm')}
                {lastUpdated ? (
                  <span className='fs-8 text-gray-500'>
                    {intl.formatMessage({ id: 'LORA.LAST_UPDATED' })}: {lastUpdated}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <div className='d-flex flex-wrap align-items-end justify-content-between gap-3 mb-5'>
              {renderDeviceSelect(false)}
              <div className='d-flex flex-wrap align-items-center gap-2'>
                {renderDayNavigation('md')}
                {datePreset === 'custom' ? (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary ms-2'
                    onClick={openFilters}
                  >
                    <i className='fa fa-calendar me-1'></i>
                    <span className='d-none d-xl-inline'>{toolbarDateLabel}</span>
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {!isMobile ? (
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
              <span className='fs-8 text-gray-500'>
                {lastUpdated
                  ? `${intl.formatMessage({ id: 'LORA.LAST_UPDATED' })}: ${lastUpdated}`
                  : ''}
              </span>
              <div className='d-flex flex-wrap align-items-center gap-2 ms-auto'>
                {renderLiveToggle(false)}
              </div>
            </div>
          ) : null}

          <Modal
            show={showFilters}
            onHide={closeFilters}
            size='lg'
            fullscreen={isMobile ? true : undefined}
          >
            <Modal.Header closeButton>
              <Modal.Title>{intl.formatMessage({ id: 'FILTERS.OPTIONS' })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{filterModalBody}</Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={closeFilters}>
                {intl.formatMessage({ id: 'COMMON.CLOSE' })}
              </Button>
              <Button variant='primary' onClick={applyFilters} disabled={isInvalidHourRange}>
                {intl.formatMessage({ id: 'LORA.APPLY', defaultMessage: 'Apply' })}
              </Button>
            </Modal.Footer>
          </Modal>

          {!selectedDevice ? (
            <div className='py-10 text-center text-muted'>
              {intl.formatMessage({ id: 'LORA.SELECT_DEVICE' })}
            </div>
          ) : (
            <>
              <LoraSummaryCards stats={stats} isLoading={isChartLoading} />

              {fetchError ? (
                <div className='alert alert-danger d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4'>
                  <span>{fetchError}</span>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger'
                    onClick={handleRetry}
                  >
                    {intl.formatMessage({ id: 'LORA.RETRY' })}
                  </button>
                </div>
              ) : null}

              {!isChartLoading && chartData.length === 0 && !fetchError ? (
                <div className='py-6 text-center text-muted mb-4'>
                  {intl.formatMessage({ id: 'NODATA' })}
                </div>
              ) : null}

              <LoraChart
                ref={chartRef}
                deviceId={selectedDevice.deviceId}
                startDate={range.startDate as Date}
                endDate={range.endDate as Date}
                isSingleDay={isSingleDay}
                initHHmm={startTime}
                endHHmm={endTime}
                onStats={handleStats}
                onCurrentDayChange={setCurrentViewDay}
                onDataChange={handleDataChange}
                onError={setFetchError}
                onLoadingChange={setIsChartLoading}
                liveMode={liveMode}
                refreshMs={60000}
                isFullscreen={isChartFullscreen}
                onFullscreenChange={setIsChartFullscreen}
                onExportCsv={handleExportCsv}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoraDashboard
